/* eslint-disable @typescript-eslint/naming-convention */
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { BehaviorSubject, forkJoin, Observable, of, Subscription, throwError } from 'rxjs';
import { UserInterface } from '../../../models/interface/user.interface';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons/faChevronDown';
import { faChevronUp } from '@fortawesome/free-solid-svg-icons/faChevronUp';
import { faSearch } from '@fortawesome/free-solid-svg-icons/faSearch';
import { faEnvelope } from '@fortawesome/free-solid-svg-icons/faEnvelope';
import { catchError, distinctUntilChanged, filter, mergeMap, observeOn, skip, take } from 'rxjs/operators';
import { faStar } from '@fortawesome/free-regular-svg-icons/faStar';
import { faTrash } from '@fortawesome/free-solid-svg-icons/faTrash';
import { Action, Store } from '@ngrx/store';
import { InterventionSharedService } from '../../../services/shared/intervention-shared/intervention-shared.service';
import { InterventionInterface } from '../../../models/interface/intervention.interface';
import { HelperService } from '../../../services/helper/helper.service';
import { PaginationListComponent } from '../../utils/pagination-list/pagination-list.component';
import { ProfileInterface } from '../../../models/interface/profile.interface';
import { HelperDialogService } from '../../../services/helper/helper-dialog/helper-dialog.service';
import { StudyActionTypes } from '../../../store/study/study.action';
import { getCollaboratorsByStudyId } from '../../../store/study/study.selector';
import { GetAllUsersAdminPayload, UserActionTypes } from 'src/app/store/user/user.action';
import { GroupSharedService } from 'src/app/services/shared/group-shared/group-shared.service';
import { StudyInterface } from 'src/app/models/interface/study/study.interface';
import { RoleInterface } from 'src/app/models/interface/role.interface';
import { OrganisationCollaboratorInterface } from 'src/app/models/interface/organisation/organisation-collaborator.interface';
import { GetOrganisationCollaboratorsSuccess, OrganisationActionTypes } from 'src/app/store/organisation/organisation.action';
import { Actions, ofType } from '@ngrx/effects';
import { HttpResponse } from '@angular/common/http';
import { UserSharedService } from 'src/app/services/shared/user-shared/user-shared.service';

type StudyCollaborators = {
  studyId: number;
  collaborators: UserInterface[];
};

/**
 * Component:
 * Collaborator page displaying a list of collaborators of the group;
 * Can be found: {uri}/interventions/{{intervention_id}}/collaborators
 */
@Component({
  selector: 'app-collaborator',
  templateUrl: './intervention-collaborator.component.html',
  styleUrls: ['./intervention-collaborator.component.scss'],
})
export class InterventionCollaboratorComponent implements OnInit, OnDestroy {
  @ViewChild('paginator') paginator: PaginationListComponent;

  // Icons
  faSearch = faSearch;
  faChevronUp = faChevronUp;
  faChevronDown = faChevronDown;
  faStar = faStar;
  faEnvelope = faEnvelope;
  faTrash = faTrash;

  // Filter
  public searchFilter = '';
  public isLoading$: Observable<boolean>;

  // List of roles
  public roles = ['study.ecoachmanager', 'study.ecoach', 'study.access'] as const;

  public myUserId: number;

  // Data provided by StudyService
  public collaborators: UserInterface[] = [];

  // Data provided by UserService
  public eCoaches: UserInterface[] = [];

  public isManager$: Observable<boolean>;
  public isOrganisationManager$: Observable<boolean>;
  public isCollapse$: Observable<boolean>;
  public collaborators$: Observable<UserInterface[]>;

  private myCollaborators: UserInterface[];

  public pagedCollaborators: UserInterface[];
  public pagedCollaborators$: Observable<UserInterface[]>;

  // Translation
  public param = { intervention_name: '...' };
  public profile$: Observable<UserInterface>;
  public profileRoles$: Observable<RoleInterface[]>;

  private isAdminSubject = new BehaviorSubject<boolean>(false);

  private users$: Observable<UserInterface[]>;

  public isCollapseSubject = new BehaviorSubject<boolean>(true);

  // study set by the parent-component intervention-detail
  public study: StudyInterface = null;
  private studyId: number;

  private searchTextSubject = new BehaviorSubject<string>('');

  private isLoadingSubject = new BehaviorSubject<boolean>(true);

  private isManagerSubject = new BehaviorSubject<boolean>(false);
  private isOrganisationManagerSubject = new BehaviorSubject<boolean>(false);
  private collaboratorsSubject = new BehaviorSubject<UserInterface[]>([]);
  private pagedCollaboratorsSubject = new BehaviorSubject<UserInterface[]>([]);

  private collaboratorsOfStudy$: Observable<StudyCollaborators>;

  private subscriptions: Subscription[] = [];

  constructor(
    private helperService: HelperService,
    private helperDialogService: HelperDialogService,
    private actions$: Actions,
    private store: Store<{
      myProfile: ProfileInterface;
      myRoles: RoleInterface[];
      users: UserInterface[];
      getCollaboratorsByStudyId: StudyCollaborators;
      allECoaches: UserInterface[];
    }>,
    private interventionSharedService: InterventionSharedService,
    private groupSharedService: GroupSharedService,
    private userSharedService: UserSharedService,
  ) {
    this.profile$ = store.select('myProfile');
    this.profileRoles$ = store.select('myRoles');
    this.users$ = this.store.select('users');
    this.isLoading$ = this.isLoadingSubject.asObservable();
    this.isManager$ = this.isManagerSubject.asObservable();
    this.isOrganisationManager$ = this.isOrganisationManagerSubject.asObservable();
    this.isCollapse$ = this.isCollapseSubject.asObservable();
    this.collaborators$ = this.collaboratorsSubject.asObservable();
    this.pagedCollaborators$ = this.pagedCollaboratorsSubject.asObservable();
  }

  public get helper() {
    return this.helperService;
  }

  public get helperDialog() {
    return this.helperDialogService;
  }

  ngOnInit(): void {
    this.subscriptions.push(
      this.groupSharedService.isOrganisationManager$.subscribe(
        (value) => this.isOrganisationManagerSubject.next(value),
      ),
    );

    this.subscriptions.push(
      this.profileRoles$.pipe(filter((roles) => roles.length > 0)).subscribe({
        next: (roles) => {
          this.isAdminSubject.next(this.helperService.hasRoles(roles, /admin$/));
        },
        error: () => {
          this.isAdminSubject.next(false);
        },
      }),
    );

    this.subscriptions.push(
      this.userSharedService.collaborators$
        .pipe(
          filter((myCollaborators) => myCollaborators !== null),
        )
        .subscribe({
          next: (myCollaborators) => {
            this.myCollaborators = myCollaborators;
          },
          error: (error) => {
            throwError(() => new Error('Error in setup:')).pipe(observeOn(error));
          },
        }),
    );

    // Sharing parent values: id and study name
    this.subscriptions.push(
      this.interventionSharedService.intervention$
        .pipe(
          filter((intervention: InterventionInterface) => intervention !== null),
          mergeMap((intervention: InterventionInterface) => {
            this.param = { intervention_name: intervention.attributes.name };
            this.studyId = intervention.attributes.study_id;
            return this.profile$.pipe(
              filter((user: UserInterface) => user !== null),
              take(1),
            );
          }),
        )
        .subscribe({
          next: (result: UserInterface) => {
            this.myUserId = result.id;
            this.applyCollaboratorFilter();
          },
          error: (error) => {
            this.applyCollaboratorFilter();
            throwError(() => error);
          },
        }),
    );
  }

  public applyCollaboratorFilter(): void {
    this.isLoadingSubject.next(true);
    this.subscriptions.push(
      this.reloadCollaboratorList(true).subscribe(() => {
        this.isLoadingSubject.next(false);
      }),
    );
  }

  public reloadCollaboratorList(setFirst?: boolean): Observable<boolean> {
    const pageIndex = this.paginator?.paginator ? this.paginator?.paginator.pageIndex : 0;
    const pageSize = this.paginator?.paginator ? this.paginator?.paginator.pageSize : 20;

    this.store.dispatch({
      type: StudyActionTypes.getCollaboratorsType,
      payload: { studyId: this.studyId, include: 'roles' },
    });
    this.collaboratorsOfStudy$ = this.store.select(getCollaboratorsByStudyId(this.studyId));
    const studyCollaborators = this.collaboratorsOfStudy$.pipe(
      filter((collaboratorsOfStudy) => !!collaboratorsOfStudy),
      take(1),
    );

    let eCoachesOrCollaborators: Observable<UserInterface[]>;
    if (this.isAdminSubject.value) {
      const payload: GetAllUsersAdminPayload = {
        roles: 'ecoach',
        isVerified: 1,
      };
      this.store.dispatch({ type: UserActionTypes.getAllUsersAdminType, payload });
      eCoachesOrCollaborators = this.users$.pipe(skip(1), take(1));
    } else {
      if (
        setFirst &&
        (!Array.isArray(this.myCollaborators) || this.myCollaborators.length === 0)
      ) {
        const organisationId = parseInt(this.study.attributes.organisation_id.toString(), 10);
        this.store.dispatch({
          type: OrganisationActionTypes.getOrganisationCollaboratorsType,
          payload: { organisationId },
        });
        eCoachesOrCollaborators = this.actions$.pipe(
          ofType(
            OrganisationActionTypes.getOrganisationCollaboratorsSuccessType,
            OrganisationActionTypes.getOrganisationCollaboratorsErrorType,
          ),
          take(1),
          mergeMap((action: Action) => {
            if (
              action.type === OrganisationActionTypes.getOrganisationCollaboratorsSuccessType
              || action.type !== OrganisationActionTypes.getOrganisationCollaboratorsErrorType
            ) {
              return of((action as GetOrganisationCollaboratorsSuccess).response);
            } else {
              return of([]);
            }
          }),
        );
      } else {
        eCoachesOrCollaborators = of(this.myCollaborators);
      }
    }
    return forkJoin({ studyCollaborators, eCoachesOrCollaborators }).pipe(
      mergeMap((results) => {
        // Collaborators of the study
        this.collaborators = results.studyCollaborators.collaborators;

        // All user with role editor
        if (this.isAdminSubject.value) {
          const result: UserInterface[] = results.eCoachesOrCollaborators instanceof HttpResponse
            ? results.eCoachesOrCollaborators.body.data
            : results.eCoachesOrCollaborators;
          this.eCoaches = this.helper.getVerifiedEditorsNotInStudy(result, this.collaborators);
          this.myCollaborators = undefined;
        } else {
          const result: OrganisationCollaboratorInterface[] = results.eCoachesOrCollaborators instanceof HttpResponse
            ? results.eCoachesOrCollaborators.body.data
            : results.eCoachesOrCollaborators;
          this.eCoaches = [];
          this.myCollaborators = this.helper.getVerifiedEditorsNotInStudy(result, this.collaborators);
        }

        // Determine my highest role
        const myself: UserInterface = this.collaborators.find((user) => user.id === this.myUserId);
        if (myself !== undefined) {
          const myRoleSlug = this.helper.getHighestRoleOfCollaborator(myself);
          if (myRoleSlug === 'study.ecoachmanager' || myRoleSlug === 'study.owner') {
            this.isManagerSubject.next(true);
          }
        }

        // If user is not manager - they can only see study.ecoachmanager and study.owner
        if (!this.isManagerSubject.value && !this.isOrganisationManagerSubject.value) {
          this.collaborators = this.collaborators.filter(
            (user) =>
              this.helperService.hasRoles(user.relationships?.roles?.data, /study\.(ecoachmanager|owner)$/) ||
              user.id.toString() === this.myUserId.toString(),
          );
        }

        // Enable search and pagination
        return this.search(this.searchFilter).pipe(
          mergeMap(() =>
            this.helper.setPagedContent(
              this.collaboratorsSubject,
              this.pagedCollaboratorsSubject,
              setFirst,
              pageIndex,
              pageSize,
            ),
          ),
        );
      }),
      catchError(
        (error) => throwError(() => new Error('ForkJoin error')).pipe(observeOn(error)),
      ),
    );
  }

  public updateList(): void {
    this.subscriptions.push(this.reloadCollaboratorList().subscribe());
  }

  public updatePagedCollaborators(event: UserInterface[]) {
    if (event) {
      this.pagedCollaboratorsSubject.next(event);
    }
  }

  public openDialogGroupCollaboratorOrganisationManager(): void {
    this.helperDialog
      .openDialogGroupCollaboratorOrganisationManager(
        this.study,
        this.collaborators,
        this.eCoaches,
        this.myCollaborators,
        this.isAdminSubject.value,
      )
      .afterClosed()
      .pipe(mergeMap(() => this.reloadCollaboratorList()))
      .subscribe(() => {});

  }

  public openDialogGroupCollaboratorAdd(): void {
    this.helperDialog
      .openDialogGroupCollaboratorAdd(
        this.param,
        this.studyId,
        this.collaborators,
        this.eCoaches,
        this.myCollaborators,
        this.isAdminSubject.value,
      )
      .afterClosed()
      .pipe(mergeMap(() => this.reloadCollaboratorList()))
      .subscribe(() => {});
  }

  public openDialogGroupCollaboratorEdit(): void {
    this.helperDialog
      .openDialogGroupCollaboratorEdit(
        this.param,
        this.studyId,
        this.collaborators,
      )
      .afterClosed()
      .pipe(mergeMap(() => this.reloadCollaboratorList()))
      .subscribe(() => {});
  }

  public openDialogGroupCollaboratorRemove(user: UserInterface): void {
    this.helperDialog
      .openDialogGroupCollaboratorRemove(this.param, this.studyId, user)
      .afterClosed()
      .pipe(mergeMap(() => this.reloadCollaboratorList()))
      .subscribe(() => {});
  }

  // Reads search value and runs searchFilter
  private search(name: string): Observable<boolean> {
    this.searchTextSubject.next(name);
    return this.searchTextSubject.pipe(
      distinctUntilChanged(),
      mergeMap(() => {
        this.collaboratorsSubject.next(
          this.helper.filterUsersBy(this.collaborators, this.searchFilter.toLowerCase().trim()),
        );
        return of(true);
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
